import { useEffect, useState, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import useViasAdmin from '../../hooks/useViasAdmin';
import DataTable from '../../components/Panel/DataTable';
import MapaSelectorCoordenadas from '../../components/Panel/MapaSelectorCoordenadas';
import BotonAccionIcono from '../../components/Panel/BotonAccionIcono';
import usePaginacionLocal from '../../hooks/usePaginacionLocal';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

// Mismos códigos que `Via.TIPOS_VIA` en el backend (`via/models.py`) — es un
// catálogo fijo, no tiene sentido traerlo por API para esto.
const TIPOS_VIA = [
  { valor: 'AV', etiqueta: 'Avenida' },
  { valor: 'CL', etiqueta: 'Calle' },
  { valor: 'PS', etiqueta: 'Pasaje' },
  { valor: 'PR', etiqueta: 'Parque' },
  { valor: 'ML', etiqueta: 'Malecón' },
  { valor: 'PL', etiqueta: 'Playa' },
  { valor: 'JR', etiqueta: 'Jirón' },
  { valor: 'PZ', etiqueta: 'Plaza' },
  { valor: 'AL', etiqueta: 'Alameda' },
  { valor: 'CR', etiqueta: 'Carrera' },
  { valor: 'TR', etiqueta: 'Transversal' },
  { valor: 'DI', etiqueta: 'Diagonal' },
  { valor: 'OT', etiqueta: 'Otro' },
];

const VIA_VACIA = {
  via_chTipo: 'AV', via_chNombre: '', via_inCuadra: '',
  via_deLatitud: '', via_deLongitud: '', via_boPar: false, via_boImpar: false,
  via_txDescripcion: '',
};

/**
 * Módulo de Vías (HU-009-B) — CRUD completo del catálogo de vías, con alta
 * de coordenadas por mapa (clic o arrastre del pin, `MapaSelectorCoordenadas`)
 * en vez de tipear lat/lng a mano, más exportar/importar en bloque por Excel
 * o JSON. Exclusiva de personal administrativo (gateada por
 * `RutaProtegidaPanel` en `PanelRoutes`; el backend además exige los
 * permisos `vias.*` puntuales, ver `permiso/migrations/0005_seed_permisos_vias.py`).
 */
const GestionVias = () => {
  const {
    loading, error, listarVias, crearVia, actualizarVia, eliminarVia,
    exportarExcel, exportarJson, importar,
  } = useViasAdmin();

  const [vias, setVias] = useState([]);
  const { datosPagina, paginacion } = usePaginacionLocal(vias);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [formulario, setFormulario] = useState(null); // null | VIA_VACIA | vía existente
  const [mensaje, setMensaje] = useState('');

  const cargar = useCallback(async () => {
    const resultado = await listarVias({ search: busqueda, tipo: filtroTipo });
    if (resultado.success) setVias(resultado.vias);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, filtroTipo]);

  useEffect(() => { cargar(); }, [cargar]);

  const esEdicion = formulario && formulario.id;

  const guardarFormulario = async (evento) => {
    evento.preventDefault();
    setMensaje('');
    const datos = {
      ...formulario,
      via_inCuadra: Number(formulario.via_inCuadra),
      via_deLatitud: Number(formulario.via_deLatitud),
      via_deLongitud: Number(formulario.via_deLongitud),
    };
    const resultado = esEdicion
      ? await actualizarVia(formulario.id, datos)
      : await crearVia(datos);

    if (resultado.success) {
      setFormulario(null);
      cargar();
    } else {
      setMensaje(resultado.error);
    }
  };

  const handleEliminar = async (via) => {
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar vía?',
      text: `"${via.direccion_completa}" — esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#e63946',
    });
    if (!confirmacion.isConfirmed) return;
    const resultado = await eliminarVia(via.id);
    if (resultado.success) cargar();
    else setMensaje(resultado.error);
  };

  const handleImportar = async (evento, formato) => {
    const archivo = evento.target.files[0];
    if (!archivo) return;
    const resultado = await importar(archivo, formato);
    setMensaje(
      resultado.success
        ? `Importación completa: ${resultado.resultado.creadas} creadas, ${resultado.resultado.duplicadas} duplicadas, ${resultado.resultado.errores} errores.`
        : resultado.error
    );
    evento.target.value = '';
    if (resultado.success) cargar();
  };

  const columnas = useMemo(() => [
    { Header: 'Dirección', accessor: 'direccion_completa' },
    { Header: 'Lados', accessor: 'lados_disponibles' },
    {
      Header: 'Coordenadas',
      id: 'coordenadas',
      accessor: (v) => `${Number(v.via_deLatitud).toFixed(6)}, ${Number(v.via_deLongitud).toFixed(6)}`,
    },
    { Header: 'Descripción', accessor: 'via_txDescripcion' },
    {
      Header: 'Acciones',
      id: 'acciones',
      Cell: ({ row }) => (
        <div className="panel-gu-fila-acciones">
          <BotonAccionIcono icono="fa-pen" variante="editar" titulo="Editar" onClick={() => setFormulario({ ...row.original })} />
          <BotonAccionIcono icono="fa-trash" variante="eliminar" titulo="Eliminar" onClick={() => handleEliminar(row.original)} />
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PanelShell>
      <div className="panel-gu-cabecera">
        <h2>Vías</h2>
        <div className="panel-gu-acciones">
          <button type="button" className="panel-boton-secundario" onClick={exportarExcel}>Exportar Excel</button>
          <button type="button" className="panel-boton-secundario" onClick={exportarJson}>Exportar JSON</button>
          <label className="panel-boton-secundario panel-gu-label-archivo">
            Importar Excel
            <input type="file" accept=".xlsx,.xls" onChange={(e) => handleImportar(e, 'excel')} hidden />
          </label>
          <label className="panel-boton-secundario panel-gu-label-archivo">
            Importar JSON
            <input type="file" accept=".json" onChange={(e) => handleImportar(e, 'json')} hidden />
          </label>
          <button type="button" className="panel-boton-header-accion" onClick={() => setFormulario({ ...VIA_VACIA })}>
            <i className="fas fa-plus"></i> Nueva vía
          </button>
        </div>
      </div>

      <div className="panel-gu-filtros">
        <input
          className="panel-input"
          placeholder="Buscar por nombre o descripción…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="panel-input">
          <option value="">Todos los tipos</option>
          {TIPOS_VIA.map((t) => <option key={t.valor} value={t.valor}>{t.etiqueta}</option>)}
        </select>
      </div>

      {mensaje && <p className="panel-gu-mensaje">{mensaje}</p>}
      {error && <p className="panel-gu-mensaje">{error}</p>}

      <DataTable
        columns={columnas}
        data={datosPagina}
        filaVacia={loading ? 'Cargando…' : 'No hay vías con estos filtros.'}
        paginacion={paginacion}
      />

      {formulario && (
        <div className="panel-gu-modal-fondo" onClick={() => setFormulario(null)}>
          <form className="panel-gu-modal panel-via-modal" onClick={(e) => e.stopPropagation()} onSubmit={guardarFormulario}>
            <h3>{esEdicion ? 'Editar vía' : 'Nueva vía'}</h3>

            <div className="panel-gu-fila-form">
              <div>
                <label className="panel-label">Tipo</label>
                <select className="panel-input" value={formulario.via_chTipo}
                  onChange={(e) => setFormulario({ ...formulario, via_chTipo: e.target.value })}>
                  {TIPOS_VIA.map((t) => <option key={t.valor} value={t.valor}>{t.etiqueta}</option>)}
                </select>
              </div>
              <div>
                <label className="panel-label">Cuadra</label>
                <input className="panel-input" type="number" required value={formulario.via_inCuadra}
                  onChange={(e) => setFormulario({ ...formulario, via_inCuadra: e.target.value })} />
              </div>
            </div>

            <label className="panel-label">Nombre</label>
            <input className="panel-input" required value={formulario.via_chNombre}
              onChange={(e) => setFormulario({ ...formulario, via_chNombre: e.target.value })} />

            <label className="panel-label">Descripción (opcional)</label>
            <textarea className="panel-input" rows={2} value={formulario.via_txDescripcion || ''}
              onChange={(e) => setFormulario({ ...formulario, via_txDescripcion: e.target.value })} />

            <div className="panel-gu-fila-form">
              <label className="panel-rol-permiso-item">
                <input type="checkbox" checked={formulario.via_boPar}
                  onChange={(e) => setFormulario({ ...formulario, via_boPar: e.target.checked })} />
                <span>Lado par</span>
              </label>
              <label className="panel-rol-permiso-item">
                <input type="checkbox" checked={formulario.via_boImpar}
                  onChange={(e) => setFormulario({ ...formulario, via_boImpar: e.target.checked })} />
                <span>Lado impar</span>
              </label>
            </div>

            <label className="panel-label">Coordenada</label>
            <MapaSelectorCoordenadas
              lat={formulario.via_deLatitud || null}
              lng={formulario.via_deLongitud || null}
              onCambiar={(lat, lng) => setFormulario({ ...formulario, via_deLatitud: lat, via_deLongitud: lng })}
            />
            {formulario.via_deLatitud && formulario.via_deLongitud && (
              <p className="panel-texto-ayuda">
                {Number(formulario.via_deLatitud).toFixed(6)}, {Number(formulario.via_deLongitud).toFixed(6)}
              </p>
            )}

            <div className="panel-gu-modal-botones">
              <button type="button" className="panel-boton-secundario" onClick={() => setFormulario(null)}>Cancelar</button>
              <button type="submit" className="panel-boton-primario" disabled={loading || !formulario.via_deLatitud}>
                {loading ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </PanelShell>
  );
};

export default GestionVias;
